/**
 * DispatchListReport (LISTS-01, on the universal engine). The ESC Dispatch List,
 * rebuilt on the SAME engine as Customers + Invoices: pick any column, filter by
 * any field, group (by Tech / Status / City / Dept) with per-group counts, save
 * views, export CSV. The Board answers "today"; this answers "all pending",
 * "everything Brent ran in May", "find that water-heater call". Rows PEEK via
 * DispatchPeek (FLOW-01 two-window model) — never navigate away.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { CustomerLink } from "@/components/links";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { WindowBadge } from "@/components/ui/WindowBadge";
import { pagedAll } from "@/lib/pagedAll";
import { supabase } from "@/lib/supabase";
import { displayCustomerName, extractJobPreview, resolveWindowAndPriority } from "@/lib/format";
import { DispatchPeek } from "@/features/customer-360/DispatchPeek";
import {
  CsvButton, DateRangeControl, ViewsMenu, ListSearch, downloadCsv, pillSelectCls, isoDaysAgo, isoToday,
  useSort, useColumns, ColumnsButton, ListTable, FiltersButton, FilterChips, applyFilters,
  type ListField, type FilterCond,
} from "./listCore";

interface Row {
  dispatch: string;
  counter: number | null;
  appointment_date: string;
  window_code: string | null;
  status: string | null;
  notes: string | null;
  custno: string;
  locno: string | null;
  locname: string | null;
  customer_fullname: string | null;
  service_address: string | null;
  city: string | null;
  tech_empno: string;
  tech_name: string | null;
  tech_first: string | null;
  tech_last: string | null;
  tech_dept: string | null;
}

interface Filters { from: string; to: string; q: string; status: string; tech: string; dept: string }
const DEFAULT_FILTERS = (): Filters => ({ from: isoDaysAgo(30), to: isoToday(), q: "", status: "", tech: "", dept: "" });
const COLS = "dispatch, counter, appointment_date, window_code, status, notes, custno, locno, locname, customer_fullname, service_address, city, tech_empno, tech_name, tech_first, tech_last, tech_dept";
const DISPLAY_CAP = 1500;

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d; }
};
const techNameOf = (r: Row) => r.tech_name ?? (`${r.tech_first ?? ""} ${r.tech_last ?? ""}`.trim() || r.tech_empno);

export function DispatchListReport() {
  const [f, setF] = useState<Filters>(DEFAULT_FILTERS);
  const [conds, setConds] = useState<FilterCond[]>([]);
  const [peek, setPeek] = useState<string | null>(null);
  const sort = useSort<Row>("date", "desc");

  const fields: ListField<Row>[] = useMemo(() => [
    { id: "date", type: "date", filterable: true, col: "appointment_date", label: "Date", value: (r) => r.appointment_date, render: (r) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(r.appointment_date)}</span> },
    { id: "dispatch", type: "text", filterable: true, col: "dispatch", label: "Dispatch #", value: (r) => r.dispatch, render: (r) => <span className="font-mono text-xs font-medium text-slate-700">{r.dispatch}</span> },
    { id: "customer", type: "text", filterable: true, col: "customer_fullname", label: "Customer", value: (r) => displayCustomerName(r.locname, r.customer_fullname), render: (r) => <CustomerLink custno={r.custno} locno={r.locno ?? undefined} className="text-sm">{displayCustomerName(r.locname, r.customer_fullname)}</CustomerLink> },
    { id: "city", type: "text", filterable: true, groupable: true, col: "city", label: "City", value: (r) => r.city },
    { id: "tech", type: "text", filterable: true, groupable: true, col: "tech_name", label: "Tech", value: (r) => techNameOf(r) },
    { id: "window", label: "Window", value: (r) => resolveWindowAndPriority(r.window_code, r.notes).timeWindow, render: (r) => { const w = resolveWindowAndPriority(r.window_code, r.notes).timeWindow; return w ? <WindowBadge code={w} /> : null; } },
    { id: "status", type: "text", filterable: true, groupable: true, col: "status", label: "Status", value: (r) => r.status ?? "Pending", render: (r) => <StatusBadge status={r.status ?? "Pending"} /> },
    { id: "what", label: "What", value: (r) => extractJobPreview(r.notes, 70), render: (r) => <span className="block max-w-[320px] truncate text-xs text-slate-600">{extractJobPreview(r.notes, 70)}</span> },
    { id: "dept", type: "text", filterable: true, groupable: true, col: "tech_dept", label: "Dept", value: (r) => r.tech_dept, defaultVisible: false },
    { id: "address", type: "text", filterable: true, col: "service_address", label: "Address", value: (r) => r.service_address, defaultVisible: false },
    { id: "custno", type: "text", filterable: true, col: "custno", label: "Cust #", value: (r) => r.custno, defaultVisible: false },
    { id: "notes", type: "text", filterable: true, col: "notes", label: "Notes", value: (r) => (r.notes ?? "").replace(/\s+/g, " ").slice(0, 300), defaultVisible: false },
  ], []);
  const cols = useColumns("dispatches", fields);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["list_dispatches", f.from, f.to, conds],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Row[]> => pagedAll<Row>(() => {
      let q = supabase.from("v_dispatch_full").select(COLS).gte("appointment_date", f.from).lte("appointment_date", f.to);
      q = applyFilters(q, conds, fields as ListField<unknown>[]);
      return q.order("appointment_date", { ascending: false }).order("dispatch", { ascending: false }).order("tech_empno", { ascending: true });
    }),
  });
  const all = data ?? [];

  const filtered = useMemo(() => {
    const ql = f.q.trim().toLowerCase();
    return all.filter((r) => {
      if (f.status && (r.status ?? "Pending") !== f.status) return false;
      if (f.tech && r.tech_empno !== f.tech) return false;
      if (f.dept && (r.tech_dept ?? "") !== f.dept) return false;
      if (ql) {
        const hay = [r.dispatch, r.customer_fullname, r.locname, r.service_address, r.city, r.notes, techNameOf(r)].join(" ").toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [all, f.q, f.status, f.tech, f.dept]);

  const opts = useMemo(() => {
    const count = (key: (r: Row) => string | null | undefined) => {
      const m = new Map<string, number>();
      for (const r of all) { const k = key(r); if (!k) continue; m.set(k, (m.get(k) ?? 0) + 1); }
      return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    };
    return {
      statuses: count((r) => r.status ?? "Pending"),
      techs: count((r) => `${r.tech_empno}|${techNameOf(r)}`),
      depts: count((r) => r.tech_dept),
    };
  }, [all]);

  const distinctDispatches = new Set(filtered.map((r) => r.dispatch)).size;
  const shownRows = filtered.slice(0, DISPLAY_CAP);
  const setPart = (p: Partial<Filters>) => setF((prev) => ({ ...prev, ...p }));

  function exportCsv() {
    const shown = cols.visible.map((id) => fields.find((x) => x.id === id)).filter(Boolean) as ListField<Row>[];
    downloadCsv(
      `dispatches_${f.from}_${f.to}.csv`,
      shown.map((c) => c.label),
      filtered.map((r) => shown.map((c) => { const v = c.value(r); return v == null ? "" : String(v); })),
    );
  }

  return (
    <div className="min-w-0 flex-1">
      {/* TOOLBAR-REFRESH — a search-forward command row + a clean scoping row */}
      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <ListSearch value={f.q} onChange={(v) => setPart({ q: v })} placeholder="Search customer, address, notes, dispatch #…" />
          <FiltersButton fields={fields} conds={conds} onChange={setConds} />
          <ColumnsButton fields={fields} cols={cols} />
          <CsvButton onClick={exportCsv} />
          <ViewsMenu entity="dispatches" currentFilters={f as unknown as Record<string, unknown>} onApply={(saved) => setF({ ...DEFAULT_FILTERS(), ...(saved as Partial<Filters>) })} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeControl from={f.from} to={f.to} onChange={(from, to) => setF((prev) => ({ ...prev, from, to }))} />
          <select value={f.status} onChange={(e) => setPart({ status: e.target.value })} className={pillSelectCls} title="Filter by status">
            <option value="">All statuses</option>
            {opts.statuses.map(([s, n]) => <option key={s} value={s}>{s} ({n})</option>)}
          </select>
          <select value={f.tech} onChange={(e) => setPart({ tech: e.target.value })} className={pillSelectCls} title="Filter by tech">
            <option value="">All techs</option>
            {opts.techs.map(([k, n]) => { const [empno, name] = k.split("|"); return <option key={empno} value={empno}>{name} ({n})</option>; })}
          </select>
          <select value={f.dept} onChange={(e) => setPart({ dept: e.target.value })} className={pillSelectCls} title="Filter by dept">
            <option value="">All depts</option>
            {opts.depts.map(([d, n]) => <option key={d} value={d}>{d} ({n})</option>)}
          </select>
          {conds.length > 0 && <FilterChips conds={conds} fields={fields} onChange={setConds} />}
          <div className="ml-auto whitespace-nowrap text-sm tabular-nums text-slate-500">
            <strong className="text-slate-800">{distinctDispatches.toLocaleString()}</strong> dispatches · {filtered.length.toLocaleString()} rows{isFetching ? " · …" : ""}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : isError ? (
        <Card className="p-6"><p className="text-sm text-red-600">{(error as Error)?.message}</p></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
            <span className="text-[14.5px] font-bold tracking-tight text-slate-900">Dispatch List</span>
            <InfoTip label="How this list works">Click a row to see the dispatch. For per-group counts, group by Tech, Status, City or Dept in the Fields menu. Save your filters as a view to reuse them.</InfoTip>
            <span className="ml-auto text-xs tabular-nums text-slate-500">{filtered.length.toLocaleString()} rows{filtered.length > DISPLAY_CAP ? ` · showing first ${DISPLAY_CAP.toLocaleString()}` : ""}</span>
          </div>
          <div className="max-h-[68vh] overflow-auto">
            <ListTable
              rows={shownRows}
              fields={fields}
              cols={cols}
              sort={sort}
              getKey={(r) => `${r.dispatch}-${r.tech_empno}-${r.counter ?? 0}`}
              onRowClick={(r) => setPeek(r.dispatch)}
              emptyText="Nothing matches — widen the dates or clear a filter."
            />
          </div>
        </Card>
      )}

      <DispatchPeek dispatchId={peek} onClose={() => setPeek(null)} />
    </div>
  );
}
